import { ReferenceManyToManyField } from "@react-admin/ra-relationships";
import {
  ImageField,
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  Labeled,
  useShowContext,
  useTranslate,
  SingleFieldList,
  ChipField,
  useResourceContext,
  BooleanField,
  FunctionField,
  useRefresh
} from "react-admin";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import { AddOnsDetailsIcon, ServiceDetailsIcon } from "assets/Icons";
import Gallery from "./Gallery";
import GalleryImage from "./GalleryImage";
import { muiColors } from "themes/colors";
import ServiceAddOnsList from "./ServiceAddOnsList";

function ServiceImages({ imageKey }: { imageKey: string }) {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (record?.[imageKey]) {
    return (
      <Labeled label={translate(`services.${imageKey.replace("_url", "")}`)}>
        <ImageField source={imageKey} />
      </Labeled>
    );
  }
  return null;
}

const ConditionalTextField = ({ sourceName }: { sourceName: string }) => {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (sourceName === "duration" && record?.[sourceName] >= 0) {
    return (
      <Labeled label={translate(`services.${sourceName}`)}>
        <NumberField source={sourceName} />
      </Labeled>
    );
  }
  if (sourceName === "description" && record?.[sourceName]?.length > 0) {
    return (
      <Labeled label={translate(`services.${sourceName}`)}>
        <TextField dangerouslySetInnerHTML={{ __html: record?.[sourceName] }} />
      </Labeled>
    );
  }

  if (sourceName === "facility" && record?.facilities?.length > 0) {
    return (
      <ReferenceManyToManyField
        reference="Facility"
        through="FacilityService"
        using="service_id,facility_id"
        label={translate("services.facilities")}
      >
        <Labeled label={translate("services.facilities")}>
          <SingleFieldList>
            <ChipField source="name" />
          </SingleFieldList>
        </Labeled>
      </ReferenceManyToManyField>
    );
  }

  if (record?.[sourceName]) {
    return (
      <Labeled label={translate(`services.${sourceName}`)}>
        <TextField source={sourceName} />
      </Labeled>
    );
  }

  return null;
};

export default function ServiceShow() {
  const translate = useTranslate();
  const resource = useResourceContext();
  const refresh = useRefresh();
  const isAddOn = resource === "AddOns";
  const imageKeys = [
    "photo_1_url",
    "photo_2_url",
    "photo_3_url",
    "photo_4_url",
    "photo_5_url"
  ];

  return (
    <Show
      actions={
        <CustomHeaderActions
          title={
            isAddOn
              ? translate("pagenames.add_on")
              : translate("pagenames.service")
          }
        />
      }
      sx={{
        "& .RaShow-card": {
          backgroundColor: muiColors.bg.default
        },
        "& .RaDatagrid-headerCell": {
          paddingTop: "16px",
          paddingBottom: "16px",
          lineHeight: "18px"
        }
      }}
    >
      <div className="w-full">
        <div className="h-min w bg-mono-primaryLightBgColor rounded-[8px] mb-4">
          <SimpleShowLayout>
            <ShowAndEditDetails
              title={
                isAddOn
                  ? translate("services.add_on_details")
                  : translate("services.service_details")
              }
              view="show"
              icon={isAddOn ? <AddOnsDetailsIcon /> : <ServiceDetailsIcon />}
            />
            <TextField
              source="name"
              label={translate("services.name")}
              fullWidth
            />
            <NumberField
              source="price"
              label={translate("services.price")}
              fullWidth
            />
            <ConditionalTextField sourceName="duration" />
            <ConditionalTextField sourceName="description" />
            {isAddOn && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px"
                }}
              >
                <Labeled label={translate("services.choice_is_required")}>
                  <BooleanField source="choice_is_required" fullWidth />
                </Labeled>
                <Labeled label={translate("services.scheduling_priority")}>
                  <TextField source="scheduling_priority" />
                </Labeled>
                {/* <ReferenceManyToManyField
                  reference="Service"
                  through="ServiceAddOn"
                  using="service_id,add_on_service_id"
                  label={translate("facility.services")}
                >
                  <Labeled label={translate("facility.services")}>
                    <SingleFieldList>
                      <ChipField source="name" />
                    </SingleFieldList>
                  </Labeled>
                </ReferenceManyToManyField> */}
              </div>
            )}
            {!isAddOn && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "8px"
                }}
              >
                <ConditionalTextField sourceName="clean_up_time" />
                <ConditionalTextField sourceName="special_conditions" />
                {/* <ConditionalTextField sourceName="facility" /> */}
                <ConditionalTextField sourceName="category" />
                <ServiceImages imageKey="logo" />
                <Gallery
                  title={translate("services.gallery_up_to_5_images") + ":"}
                >
                  {imageKeys.map((imageKey, index) => (
                    <GalleryImage
                      key={imageKey}
                      source={imageKey}
                      height="140px"
                      badgeNumber={index + 1}
                    />
                  ))}
                </Gallery>
              </div>
            )}
          </SimpleShowLayout>
        </div>
        {!isAddOn && (
          <div className="h-min w-full bg-mono-primaryLightBgColor rounded-[8px] py-2 px-4">
            <ShowAndEditDetails
              title={translate("common.add_ons")}
              view="show"
              icon={<AddOnsDetailsIcon />}
              hasEdit={false}
            />
            <FunctionField
              render={(record: any) => (
                <ServiceAddOnsList
                  addOnsData={record?.add_ons}
                  refresh={() => refresh()}
                />
              )}
            />
          </div>
        )}
      </div>
    </Show>
  );
}
